/**
 * Vesper Notification Service (Shared)
 */

import apiClient from './api.client';
import { API } from '../constants/api';
import { deduped, bust } from './dedupedFetch';
import type { NotificationsResponse, UnreadCountResponse } from '../types/notification.types';

export async function getNotifications(page = 0): Promise<NotificationsResponse> {
  const res = await apiClient.get<{ success: true; data: NotificationsResponse }>(
    `${API.NOTIFICATIONS}?page=${page}`,
  );
  return res.data.data;
}

/** 3s TTL — short so the badge feels live, long enough that BottomNav's
 *  per-pathname effect doesn't fan out a fetch for every tab change. */
export async function getUnreadCount(): Promise<number> {
  return deduped('me:unreadCount', async () => {
    const res = await apiClient.get<{ success: true; data: UnreadCountResponse }>(
      API.NOTIFICATIONS_UNREAD_COUNT,
    );
    return res.data.data.unreadCount;
  }, 3_000);
}

/** Per-type unread breakdown — drives per-section subtle badges on
 *  surfaces like the Rooms page. Returns empty object when the inbox is
 *  fully read. */
export async function getUnreadCountByType(): Promise<Record<string, number>> {
  return deduped('me:unreadByType', async () => {
    const res = await apiClient.get<{ success: true; data: { byType: Record<string, number> } }>(
      API.NOTIFICATIONS_UNREAD_BY_TYPE,
    );
    return res.data.data.byType ?? {};
  }, 3_000);
}

export async function markNotificationRead(id: string): Promise<void> {
  await apiClient.post(API.NOTIFICATION_READ(id));
  bust('me:unreadCount');
  bust('me:unreadByType');
}

/** Marks every unread notification with the given source_id as read.
 *  Call this when the user opens the surface those notifications point at
 *  (event chat, DM thread, matching room) so the badge clears immediately. */
export async function markNotificationsForSource(sourceId: string): Promise<void> {
  await apiClient.post(API.NOTIFICATIONS_SOURCE_READ(sourceId));
  bust('me:unreadCount');
  bust('me:unreadByType');
}

export async function markAllRead(): Promise<void> {
  await apiClient.post(API.NOTIFICATIONS_READ_ALL);
  bust('me:unreadCount');
  bust('me:unreadByType');
}

export async function rerollPrompt(promptId: string): Promise<void> {
  await apiClient.post(API.EVENT_PROMPT_REROLL(promptId));
}

export async function reschedulePrompt(
  promptId: string,
  timeStart: string,
  timeEnd?: string,
): Promise<void> {
  await apiClient.post(API.EVENT_PROMPT_RESCHEDULE(promptId), { timeStart, timeEnd });
}
